import { useTranslation } from 'react-i18next';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import { SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ActionButton } from '@/shared/ui/action-button';
import { Badge } from '@/components/ui/badge';

import { ColumnConfig, ColumnPreset } from '../types';
import { SortableColumnItem } from './sortable-column-item';

interface ColumnCustomizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  columnConfigs: ColumnConfig[];
  onReorderColumns: (activeId: string, overId: string) => void;
  onToggleColumnVisibility: (columnId: string) => void;
  onSave: () => void;
  onCancel: () => void;
  onReset: () => void;
  onRollback: () => void;
  onApplyPreset: (columnIds: string[]) => void;
  presets?: ColumnPreset[];
}

export function ColumnCustomizationDialog({
  open,
  onOpenChange,
  columnConfigs,
  onReorderColumns,
  onToggleColumnVisibility,
  onSave,
  onCancel,
  onReset,
  onApplyPreset,
  presets = []
}: ColumnCustomizationDialogProps) {
  const { t } = useTranslation();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (active.id && over?.id && active.id !== over.id) {
      onReorderColumns(String(active.id), String(over.id));
    }
  };

  const visibleCount = columnConfigs.filter((c) => c.visible).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader className="px-8 pb-4 relative bg-white">
          <div className="flex items-center gap-5">
            <DialogTitle className="text-2xl font-bold text-foreground tracking-tight">
              {t('gen.customize.cols.label', 'Customize columns')}
            </DialogTitle>
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold text-muted-foreground">
              {visibleCount} / {columnConfigs.length} {t('common.visible', 'visible')}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground mt-4 font-medium">
            {t('common.dragToReorder', 'Drag to reorder')}
          </p>
          <div className="h-[1px] bg-border w-full mt-4" />
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-8 py-0 custom-scrollbar bg-white">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={columnConfigs.map((c) => c.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-0 pb-4">
                {columnConfigs.map((column) => (
                  <SortableColumnItem key={column.id} column={column} onToggle={onToggleColumnVisibility} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <DialogFooter className="flex flex-row flex-wrap px-8 pt-6 border-t items-center bg-white">
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-4 flex-wrap">
              {presets.map((preset) => (
                <Button
                  key={preset.id}
                  variant="link"
                  className="p-0 h-auto"
                  onClick={() => onApplyPreset(preset.columnIds)}>
                  {preset.label}
                </Button>
              ))}
              <Button
                variant="link"
                className="p-0 h-auto text-muted-foreground hover:text-foreground"
                onClick={onReset}>
                {t('gen.customize.reset.button', 'Reset to default')}
              </Button>
            </div>
          </div>

          <div className="flex items-center ml-auto gap-3">
            <ActionButton intent="dismiss" onClick={onCancel} className="h-10 px-6 font-bold">
              {t('gen.cancel.label', 'Cancel')}
            </ActionButton>
            <ActionButton intent="primary" onClick={onSave} className="h-10 px-6 font-bold">
              {t('common.apply', 'Apply')}
            </ActionButton>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
